/* eslint-disable camelcase */
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Checkbox,
  HStack,
  Icon,
  Stack,
  Text,
  useDisclosure,
  Input,
  VStack
} from '@chakra-ui/react';
import { memo, useState, useEffect } from 'react';
import { NumberParam, useQueryParam, withDefault } from 'use-query-params';

import { useInitiatives } from '@services/query-services/initiatives.query-service';
import { useContentTypeOptions, useFeedPageFilters } from '@util/hooks';
import { dateRangeMenuItems } from '@util/constants';
import { useUserStore } from '@store/app.store';

import FeedRangepicker, { SelectedDateRange } from './feed-rangepicker.component';

function FeedFilterAccordionStack({ tab }) {
  const user = useUserStore((state) => state?.user);
  const tenant = useUserStore((state) => state.tenant);
  const isGuest = user?.isGuest;
  const { filters, handleFilterChange } = useFeedPageFilters();
  const [startDate, setStartDateRaw] = useQueryParam(
    'createdAtFrom',
    withDefault(NumberParam, null)
  );
  const [endDate, setEndDateRaw] = useQueryParam('createdAtTo', withDefault(NumberParam, null));
  const setStartDate = (value) => setStartDateRaw(value, 'replaceIn');
  const setEndDate = (value) => setEndDateRaw(value, 'replaceIn');

  const { data: focuses } = useInitiatives({ type: 'Focus' }, { enabled: !!(user && !isGuest) });
  const contentTypeFilters = useContentTypeOptions();

  // Local state for debounced inputs
  const [localParticipantName, setLocalParticipantName] = useState(filters.participantName || '');
  const [localParticipantEmail, setLocalParticipantEmail] = useState(
    filters.participantEmail || ''
  );
  const [localParticipantCompanyName, setLocalParticipantCompanyName] = useState(
    filters.participantCompanyName || ''
  );
  const [localParticipantCompanyDomain, setLocalParticipantCompanyDomain] = useState(
    filters.participantCompanyDomain || ''
  );

  // Sync local state with URL params when they change (e.g., from navigation)
  useEffect(() => {
    setLocalParticipantName(filters.participantName || '');
  }, [filters.participantName]);

  useEffect(() => {
    setLocalParticipantEmail(filters.participantEmail || '');
  }, [filters.participantEmail]);

  useEffect(() => {
    setLocalParticipantCompanyName(filters.participantCompanyName || '');
  }, [filters.participantCompanyName]);

  useEffect(() => {
    setLocalParticipantCompanyDomain(filters.participantCompanyDomain || '');
  }, [filters.participantCompanyDomain]);

  // Debounced update of URL params when local values change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleFilterChange(
        'participantName',
        localParticipantName === '' ? null : localParticipantName
      );
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [localParticipantName, handleFilterChange]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleFilterChange(
        'participantEmail',
        localParticipantEmail === '' ? null : localParticipantEmail
      );
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [localParticipantEmail, handleFilterChange]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleFilterChange(
        'participantCompanyName',
        localParticipantCompanyName === '' ? null : localParticipantCompanyName
      );
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [localParticipantCompanyName, handleFilterChange]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleFilterChange(
        'participantCompanyDomain',
        localParticipantCompanyDomain === '' ? null : localParticipantCompanyDomain
      );
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [localParticipantCompanyDomain, handleFilterChange]);

  const disclosure = useDisclosure();
  const rangePickerDisclosure = {
    ...disclosure,
    onClose: (confirm = false) => {
      if (!confirm) {
        handleFilterChange('dateRange', 'All Dates');
      }
      disclosure.onClose();
    }
  };

  const handleFocusChecked = (e, focusId) => {
    const checked = e?.target?.checked;
    const newFocusFilters = checked
      ? [...filters.focuses, focusId]
      : filters.focuses.filter((f) => f !== focusId);
    handleFilterChange('focuses', newFocusFilters);
  };

  const handleContentTypeChecked = (e, contentType) => {
    const { sub_sources } = contentType;
    const checked = e?.target?.checked;
    const newSubSourcesFilter = checked
      ? [...filters.contentTypeSubSources, ...sub_sources]
      : filters.contentTypeSubSources.filter((subSource) => !sub_sources.includes(subSource));
    handleFilterChange('contentTypeSubSources', newSubSourcesFilter);
  };

  const handleDateRangeSelect = (dateRange) => {
    handleFilterChange('dateRange', dateRange.name);
    if (dateRange.createdAtFrom) {
      setStartDate(dateRange.createdAtFrom.getTime());
    }
  };

  const selectedContentTypesCount = contentTypeFilters.filter((contentType) =>
    contentType.sub_sources.every((subSource) =>
      filters?.contentTypeSubSources?.includes(subSource)
    )
  ).length;

  const selectedCategoriesCount = filters?.focuses?.length || 0;

  const selectedPeopleCount = [localParticipantName, localParticipantEmail].filter(
    (field) => field && field.trim() !== ''
  ).length;

  const selectedCompaniesCount = [
    localParticipantCompanyName,
    localParticipantCompanyDomain
  ].filter((field) => field && field.trim() !== '').length;

  const selectedSavedArchivedCount = [filters.savedFilter, filters.archivedFilter].filter(
    Boolean
  ).length;

  return (
    <Box
      bg='white'
      border='1px solid'
      borderColor='gray.200'
      borderRadius='lg'
      overflow='hidden'
    >
      <Accordion allowMultiple>
        {/* Content Types */}
        {tab === 'summaries' && !isGuest && (
          <AccordionItem border='none'>
            <AccordionButton
              py={4}
              px={6}
              _hover={{ bg: 'gray.50' }}
              borderBottom='1px solid'
              borderColor='gray.200'
            >
              <Box
                flex='1'
                textAlign='left'
              >
                <Text
                  fontSize='md'
                  fontWeight='500'
                  color='gray.900'
                >
                  Content Types
                  {selectedContentTypesCount > 0 && (
                    <Text
                      as='span'
                      color='surface.outline'
                      fontSize='sm'
                      ml={1}
                      data-testid='content-types-count'
                    >
                      ({selectedContentTypesCount})
                    </Text>
                  )}
                </Text>
              </Box>
              <AccordionIcon color='gray.500' />
            </AccordionButton>
            <AccordionPanel
              pb={4}
              px={6}
            >
              <Stack spacing={3}>
                {contentTypeFilters.map((contentType) => (
                  <HStack
                    key={contentType?.title}
                    spacing={3}
                    py={2}
                  >
                    <Icon
                      as={contentType.icon}
                      color='gray.500'
                      boxSize={5}
                    />
                    <Text
                      fontSize='sm'
                      color='gray.700'
                      flex='1'
                    >
                      {contentType?.title}
                    </Text>
                    <Checkbox
                      isChecked={contentType.sub_sources.every((subSource) =>
                        filters?.contentTypeSubSources?.includes(subSource)
                      )}
                      onChange={(e) => handleContentTypeChecked(e, contentType)}
                      colorScheme='blue'
                      size='md'
                    />
                  </HStack>
                ))}
              </Stack>
            </AccordionPanel>
          </AccordionItem>
        )}

        {/* Categories */}
        {tab !== 'meetings' && !isGuest && (
          <AccordionItem border='none'>
            <AccordionButton
              py={4}
              px={6}
              _hover={{ bg: 'gray.50' }}
              borderBottom='1px solid'
              borderColor='gray.200'
            >
              <Box
                flex='1'
                textAlign='left'
              >
                <Text
                  fontSize='md'
                  fontWeight='500'
                  color='gray.900'
                >
                  Categories
                  {selectedCategoriesCount > 0 && (
                    <Text
                      as='span'
                      color='surface.outline'
                      fontSize='sm'
                      ml={1}
                      data-testid='categories-count'
                    >
                      ({selectedCategoriesCount})
                    </Text>
                  )}
                </Text>
              </Box>
              <AccordionIcon color='gray.500' />
            </AccordionButton>
            <AccordionPanel
              pb={4}
              px={6}
            >
              <Stack spacing={3}>
                {(focuses ?? []).map((focus) => (
                  <HStack
                    key={focus?.id}
                    spacing={3}
                    py={2}
                  >
                    <Text
                      fontSize='sm'
                      color='gray.700'
                      flex='1'
                    >
                      {focus?.title}
                    </Text>
                    <Checkbox
                      isChecked={filters?.focuses?.includes(focus?.id)}
                      onChange={(e) => handleFocusChecked(e, focus?.id)}
                      colorScheme='blue'
                      size='md'
                      data-testid='focus-checkbox'
                    />
                  </HStack>
                ))}
              </Stack>
            </AccordionPanel>
          </AccordionItem>
        )}

        {/* People */}
        {!isGuest && (
          <AccordionItem border='none'>
            <AccordionButton
              py={4}
              px={6}
              _hover={{ bg: 'gray.50' }}
              borderBottom='1px solid'
              borderColor='gray.200'
            >
              <Box
                flex='1'
                textAlign='left'
              >
                <Text
                  fontSize='md'
                  fontWeight='500'
                  color='gray.900'
                >
                  People
                  {selectedPeopleCount > 0 && (
                    <Text
                      as='span'
                      color='surface.outline'
                      fontSize='sm'
                      ml={1}
                      data-testid='people-count'
                    >
                      ({selectedPeopleCount})
                    </Text>
                  )}
                </Text>
              </Box>
              <AccordionIcon color='gray.500' />
            </AccordionButton>
            <AccordionPanel
              pt={4}
              pb={4}
              px={6}
            >
              <VStack
                spacing={4}
                align='stretch'
              >
                <Box>
                  <Text
                    size='xs'
                    fontWeight={300}
                    fontSize={14}
                  >
                    Participant Name
                  </Text>
                  <Input
                    size='sm'
                    w='full'
                    fontSize='sm'
                    h={10}
                    p={5}
                    value={localParticipantName}
                    onChange={(e) => setLocalParticipantName(e.target.value)}
                    borderRadius='md'
                    placeholder='Enter a participant name'
                    _placeholder={{
                      color: 'surface.outline',
                      p: 0.5,
                      ml: 1,
                      textStyle: 'body-500-sm'
                    }}
                  />
                </Box>
                <Box>
                  <Text
                    size='xs'
                    fontWeight={300}
                    fontSize={14}
                  >
                    Participant Email
                  </Text>
                  <Input
                    size='sm'
                    w='full'
                    fontSize='sm'
                    h={10}
                    p={5}
                    value={localParticipantEmail}
                    onChange={(e) => setLocalParticipantEmail(e.target.value)}
                    borderRadius='md'
                    placeholder='Enter a participant email'
                    _placeholder={{
                      color: 'surface.outline',
                      p: 0.5,
                      ml: 1,
                      textStyle: 'body-500-sm'
                    }}
                  />
                </Box>
              </VStack>
            </AccordionPanel>
          </AccordionItem>
        )}

        {/* Companies */}
        {!isGuest && (
          <AccordionItem border='none'>
            <AccordionButton
              py={4}
              px={6}
              _hover={{ bg: 'gray.50' }}
              borderBottom='1px solid'
              borderColor='gray.200'
            >
              <Box
                flex='1'
                textAlign='left'
              >
                <Text
                  fontSize='md'
                  fontWeight='500'
                  color='gray.900'
                >
                  Companies
                  {selectedCompaniesCount > 0 && (
                    <Text
                      as='span'
                      color='surface.outline'
                      fontSize='sm'
                      ml={1}
                      data-testid='companies-count'
                    >
                      ({selectedCompaniesCount})
                    </Text>
                  )}
                </Text>
              </Box>
              <AccordionIcon color='gray.500' />
            </AccordionButton>
            <AccordionPanel
              pb={4}
              px={6}
            >
              <VStack
                spacing={4}
                align='stretch'
              >
                <Box>
                  <Text
                    size='xs'
                    fontWeight={300}
                    fontSize={14}
                  >
                    Company Name
                  </Text>
                  <Input
                    size='sm'
                    w='full'
                    fontSize='sm'
                    h={10}
                    p={5}
                    value={localParticipantCompanyName}
                    onChange={(e) => setLocalParticipantCompanyName(e.target.value)}
                    borderRadius='md'
                    placeholder='Enter a company name'
                    _placeholder={{
                      color: 'surface.outline',
                      p: 0.5,
                      ml: 1,
                      textStyle: 'body-500-sm'
                    }}
                  />
                </Box>
                <Box>
                  <Text
                    size='xs'
                    fontWeight={300}
                    fontSize={14}
                  >
                    Company Domain
                  </Text>
                  <Input
                    size='sm'
                    w='full'
                    fontSize='sm'
                    h={10}
                    p={5}
                    value={localParticipantCompanyDomain}
                    onChange={(e) => setLocalParticipantCompanyDomain(e.target.value)}
                    borderRadius='md'
                    // placeholder={'Enter a company domain (e.g. "gmail.com")'}
                    placeholder={`Enter a company domain (e.g. ${
                      tenant.domains?.length ? `"${tenant.domains[0]}"` : 'gmail.com'
                    })`}
                    _placeholder={{
                      color: 'surface.outline',
                      p: 0.5,
                      ml: 1,
                      textStyle: 'body-500-sm'
                    }}
                  />
                </Box>
              </VStack>
            </AccordionPanel>
          </AccordionItem>
        )}

        {/* Date Range */}
        <AccordionItem border='none'>
          <AccordionButton
            py={4}
            px={6}
            _hover={{ bg: 'gray.50' }}
            borderBottom='1px solid'
            borderColor='gray.200'
          >
            <Box
              flex='1'
              textAlign='left'
            >
              <Text
                fontSize='md'
                fontWeight='500'
                color='gray.900'
              >
                Date Range
                {filters?.dateRange && filters.dateRange !== 'All Dates' && (
                  <Text
                    as='span'
                    color='surface.outline'
                    fontSize='sm'
                    ml={1}
                    data-testid='date-range-selection'
                  >
                    ({filters.dateRange})
                  </Text>
                )}
              </Text>
            </Box>
            <AccordionIcon color='gray.500' />
          </AccordionButton>
          <AccordionPanel
            pb={4}
            px={6}
          >
            <Stack spacing={3}>
              {dateRangeMenuItems.map((dateRange) => (
                <HStack
                  key={dateRange.name}
                  spacing={3}
                  py={2}
                  cursor='pointer'
                  onClick={() => handleDateRangeSelect(dateRange)}
                  _hover={{ bg: 'gray.50' }}
                  borderRadius='md'
                  px={2}
                >
                  <Text
                    fontSize='sm'
                    color='gray.700'
                    flex='1'
                  >
                    {dateRange.name}
                  </Text>
                  <Checkbox
                    isChecked={filters?.dateRange === dateRange.name}
                    onChange={() => handleDateRangeSelect(dateRange)}
                    colorScheme='blue'
                    size='md'
                  />
                </HStack>
              ))}
              <HStack
                spacing={3}
                py={2}
                cursor='pointer'
                _hover={{ bg: 'gray.50' }}
                borderRadius='md'
                px={2}
                onClick={() => {
                  handleFilterChange('dateRange', 'Custom');
                  rangePickerDisclosure.onOpen();
                }}
              >
                <Text
                  fontSize='sm'
                  color='gray.700'
                  flex='1'
                >
                  Custom...
                </Text>
                <Checkbox
                  isChecked={filters?.dateRange === 'Custom'}
                  onChange={() => {
                    handleFilterChange('dateRange', 'Custom');
                    rangePickerDisclosure.onOpen();
                  }}
                  colorScheme='blue'
                  size='md'
                />
              </HStack>
              {filters?.dateRange === 'Custom' && (
                <Box mt={4}>
                  <SelectedDateRange
                    startDate={startDate}
                    endDate={endDate}
                    onClick={rangePickerDisclosure.onOpen}
                  />
                </Box>
              )}
            </Stack>
          </AccordionPanel>
        </AccordionItem>

        {/* Saved & Archived */}
        {!isGuest && (
          <AccordionItem border='none'>
            <AccordionButton
              py={4}
              px={6}
              _hover={{ bg: 'gray.50' }}
            >
              <Box
                flex='1'
                textAlign='left'
              >
                <Text
                  fontSize='md'
                  fontWeight='500'
                  color='gray.900'
                >
                  Saved & Archived
                  {selectedSavedArchivedCount > 0 && (
                    <Text
                      as='span'
                      color='surface.outline'
                      fontSize='sm'
                      ml={1}
                      data-testid='saved-archived-count'
                    >
                      ({selectedSavedArchivedCount})
                    </Text>
                  )}
                </Text>
              </Box>
              <AccordionIcon color='gray.500' />
            </AccordionButton>
            <AccordionPanel
              pb={4}
              px={6}
            >
              <Stack spacing={3}>
                <HStack
                  spacing={3}
                  py={2}
                >
                  <Text
                    fontSize='sm'
                    color='gray.700'
                    flex='1'
                  >
                    Saved Summaries
                  </Text>
                  <Checkbox
                    isChecked={filters.savedFilter}
                    onChange={(e) => {
                      handleFilterChange('savedFilter', e.target.checked ? true : null);
                    }}
                    colorScheme='blue'
                    size='md'
                    data-testid='saved-filter-checkbox'
                  />
                </HStack>
                <HStack
                  spacing={3}
                  py={2}
                >
                  <Text
                    fontSize='sm'
                    color='gray.700'
                    flex='1'
                  >
                    Archived Summaries
                  </Text>
                  <Checkbox
                    isChecked={filters.archivedFilter}
                    onChange={(e) => {
                      handleFilterChange('archivedFilter', e.target.checked ? true : null);
                    }}
                    colorScheme='blue'
                    size='md'
                    data-testid='archived-filter-checkbox'
                  />
                </HStack>
              </Stack>
            </AccordionPanel>
          </AccordionItem>
        )}
      </Accordion>

      <FeedRangepicker
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        disclosure={rangePickerDisclosure}
      />
    </Box>
  );
}

export default memo(FeedFilterAccordionStack);
